import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function TemplateDetail({ template, onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    externalUrl: '',
    dueOffsetDays: 0,
    dependsOn: '',
    requiresEvidence: false,
    isRemovableByUser: false,
  });
  const [error, setError] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const data = await api.get(`/template-items?template_id=${template.id}`);
      setItems(data);
    } catch (err) {
      console.error('Failed to load items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenItemModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        description: item.description || '',
        externalUrl: item.externalUrl || '',
        dueOffsetDays: item.dueOffsetDays || 0,
        dependsOn: item.dependsOn || '',
        requiresEvidence: item.requiresEvidence || false,
        isRemovableByUser: item.isRemovableByUser || false,
      });
    } else {
      setEditingItem(null);
      const maxSortOrder = items.length > 0 ? Math.max(...items.map(i => i.sortOrder)) : 0;
      setFormData({
        title: '',
        description: '',
        externalUrl: '',
        dueOffsetDays: 0,
        dependsOn: '',
        requiresEvidence: false,
        isRemovableByUser: false,
        sortOrder: maxSortOrder + 1,
      });
    }
    setError('');
    setShowItemModal(true);
  };

  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setEditingItem(null);
    setError('');
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const itemData = {
        ...formData,
        templateId: template.id,
        sortOrder: editingItem ? editingItem.sortOrder : formData.sortOrder,
      };

      if (editingItem) {
        await api.put(`/template-items/${editingItem.id}`, itemData);
      } else {
        await api.post('/template-items', itemData);
      }
      await loadItems();
      handleCloseItemModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await api.delete(`/template-items/${itemId}`);
      await loadItems();
    } catch (err) {
      alert('Failed to delete item: ' + err.message);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      // Update sortOrder for all affected items
      const reorderData = newItems.map((item, index) => ({
        id: item.id,
        sortOrder: index + 1,
      }));

      try {
        await api.post('/template-items/reorder', reorderData);
      } catch (err) {
        console.error('Failed to reorder:', err);
        // Reload to restore correct order
        await loadItems();
      }
    }
  };

  const handlePreview = async () => {
    try {
      const data = await api.get(`/templates/${template.id}/preview`);
      setPreviewData(data);
      setShowPreview(true);
    } catch (err) {
      alert('Failed to load preview: ' + err.message);
    }
  };

  const getItemName = (itemId) => {
    const item = items.find((i) => i.id === itemId);
    return item ? item.title : 'Unknown';
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (showPreview && previewData) {
    return (
      <div>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn btn-secondary" onClick={() => setShowPreview(false)} data-testid="close-preview-btn">
              &larr; Back to Editor
            </button>
            <div>
              <h1 className="page-title">Preview: {previewData.template.name}</h1>
              <p className="page-subtitle">How users will see this template</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Template Information</h2>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Description:</strong>
              <p style={{ marginTop: '0.5rem' }}>{previewData.template.description}</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Type</span>
                <p style={{ fontWeight: '500' }}>{previewData.template.type}</p>
              </div>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Items</span>
                <p style={{ fontWeight: '500' }}>{previewData.total_items}</p>
              </div>
              <div>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Items Requiring Evidence</span>
                <p style={{ fontWeight: '500' }}>{previewData.items_requiring_evidence}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Checklist Items ({previewData.items.length})</h2>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {previewData.items.map((item, index) => (
              <div
                key={item.id}
                style={{
                  padding: '1rem',
                  borderBottom: index < previewData.items.length - 1 ? '1px solid var(--gray-200)' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                  <div style={{
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    backgroundColor: 'var(--gray-200)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      {item.title}
                      {item.requiresEvidence && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem' }}>📸</span>}
                      {item.externalUrl && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem' }}>🔗</span>}
                    </h3>
                    {item.description && (
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                        {item.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                      {item.dueOffsetDays !== null && <span>Due: +{item.dueOffsetDays} days</span>}
                      {item.dependsOn && <span>Depends on: {getItemName(item.dependsOn)}</span>}
                      {item.isRemovableByUser && <span className="badge badge-warning">Optional</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn btn-secondary" onClick={onBack} data-testid="back-to-templates-btn">
              &larr; Back to Templates
            </button>
            <div>
              <h1 className="page-title">{template.name}</h1>
              <p className="page-subtitle">{template.description}</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={handlePreview} data-testid="preview-template-btn">
            👁️ Preview
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Template Items ({items.length})</h2>
          <button className="btn btn-primary" onClick={() => handleOpenItemModal()} data-testid="add-item-btn">
            + Add Item
          </button>
        </div>

        {items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <h3 className="empty-state-title">No Items Yet</h3>
            <p className="empty-state-description">Start building your checklist by adding items.</p>
            <button className="btn btn-primary" onClick={() => handleOpenItemModal()}>Add First Item</button>
          </div>
        ) : (
          <div style={{ padding: '1.5rem' }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                {items.map((item, index) => (
                  <SortableItem key={item.id} id={item.id}>
                    <div
                      style={{
                        padding: '1rem',
                        marginBottom: '0.5rem',
                        backgroundColor: 'var(--white)',
                        border: '1px solid var(--gray-300)',
                        borderRadius: '6px',
                        cursor: 'grab',
                      }}
                      data-testid={`item-${item.id}`}
                    >
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'start' }}>
                        <div style={{
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '4px',
                          backgroundColor: 'var(--primary-blue-100)',
                          color: 'var(--primary-blue-700)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          flexShrink: 0,
                        }}>
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                            {item.title}
                            {item.requiresEvidence && <span style={{ marginLeft: '0.5rem' }}>📸</span>}
                            {item.externalUrl && <span style={{ marginLeft: '0.5rem' }}>🔗</span>}
                            {item.isRemovableByUser && <span className="badge badge-warning" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>Optional</span>}
                          </h3>
                          {item.description && (
                            <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                              {item.description.substring(0, 100)}{item.description.length > 100 ? '...' : ''}
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                            <span>Due: +{item.dueOffsetDays || 0} days</span>
                            {item.dependsOn && <span>Depends on: {getItemName(item.dependsOn)}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                          <button
                            className="btn btn-secondary btn-small"
                            onClick={() => handleOpenItemModal(item)}
                            data-testid={`edit-item-${item.id}`}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger btn-small"
                            onClick={() => handleDeleteItem(item.id)}
                            data-testid={`delete-item-${item.id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </SortableItem>
                ))}
              </SortableContext>
            </DndContext>
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--info-100)', borderRadius: '6px' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--info-600)' }}>
                💡 <strong>Tip:</strong> Drag and drop items to reorder them. The order will be saved automatically.
              </p>
            </div>
          </div>
        )}
      </div>

      {showItemModal && (
        <div className="modal-overlay" onClick={handleCloseItemModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
            </div>
            <form onSubmit={handleSubmitItem}>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}
                
                <div className="form-group">
                  <label className="form-label" htmlFor="title">Title *</label>
                  <input
                    id="title"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Complete HR paperwork"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="item-title-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="description">Description</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Detailed instructions for this item..."
                    data-testid="item-description-editor"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="externalUrl">External URL (Optional)</label>
                  <input
                    id="externalUrl"
                    type="url"
                    className="form-input"
                    placeholder="https://training.example.com/course"
                    value={formData.externalUrl}
                    onChange={(e) => setFormData({ ...formData, externalUrl: e.target.value })}
                    data-testid="item-externalUrl-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="dueOffsetDays">Due Date Offset (Days)</label>
                  <input
                    id="dueOffsetDays"
                    type="number"
                    className="form-input"
                    placeholder="0"
                    value={formData.dueOffsetDays}
                    onChange={(e) => setFormData({ ...formData, dueOffsetDays: parseInt(e.target.value) || 0 })}
                    data-testid="item-dueOffsetDays-input"
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                    Number of days after start date when this item is due
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="dependsOn">Depends On (Optional)</label>
                  <select
                    id="dependsOn"
                    className="form-select"
                    value={formData.dependsOn}
                    onChange={(e) => setFormData({ ...formData, dependsOn: e.target.value })}
                    data-testid="item-dependsOn-select"
                  >
                    <option value="">No Dependency</option>
                    {items
                      .filter((i) => i.id !== editingItem?.id)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.requiresEvidence}
                      onChange={(e) => setFormData({ ...formData, requiresEvidence: e.target.checked })}
                      style={{ marginRight: '0.5rem' }}
                      data-testid="item-requiresEvidence-checkbox"
                    />
                    <span className="form-label" style={{ marginBottom: 0 }}>Requires Evidence (photo/document)</span>
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.isRemovableByUser}
                      onChange={(e) => setFormData({ ...formData, isRemovableByUser: e.target.checked })}
                      style={{ marginRight: '0.5rem' }}
                      data-testid="item-isRemovableByUser-checkbox"
                    />
                    <span className="form-label" style={{ marginBottom: 0 }}>Can be skipped by user</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseItemModal} data-testid="cancel-item-btn">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="save-item-btn">
                  {editingItem ? 'Save Changes' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateDetail;
